import { useRef } from 'react';
import {
  doc, setDoc, updateDoc, addDoc, collection, runTransaction,
  arrayUnion, arrayRemove
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { diffProfile, needsTransaction, buildUpdate } from '../utils/profileSync';
import { useAutosave } from './useAutosave';

export {
  TYPING_IDLE_MS, CHANGE_DEBOUNCE_MS, RETRY_DELAYS_MS, SAVED_MARK_MS,
} from './useAutosave';

// En redigeringssession tar slut efter så här lång tid utan ändringar
export const SESSION_IDLE_MS = 10 * 60 * 1000;

const clone = v => JSON.parse(JSON.stringify(v));

function versionLabel(prefix) {
  const now = new Date();
  return prefix + ' ' + now.toLocaleDateString('sv-SE') + ' ' +
    now.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
}

// Fälten som speglas till medarbetarregistret (users)
function userSyncFields(personal = {}) {
  const out = {};
  if (personal.name)     out.displayName = personal.name;
  if (personal.title)    out.title       = personal.title;
  if (personal.location) out.location    = personal.location;
  if (personal.shortName !== undefined) out.shortName = personal.shortName;
  return out;
}

/**
 * Automatisk sparning av profiles/{uid}.
 * `current` är profilens lokala läge (innehåll + profiltyp/blockordning/blockval).
 * Kö, fördröjning, omförsök och kvittenser sköts av useAutosave.
 */
export function useProfileAutosave({ uid, editorUid, current }) {
  const latestRef = useRef(current);
  latestRef.current = current;
  const baselineRef = useRef(null);     // senast sparade/lästa läget
  const existsRef = useRef(true);
  const userSyncRef = useRef(null);
  const sessionRef = useRef(null);      // { start, versioned, lastChangeAt }

  function hasChanges() {
    return baselineRef.current !== null
      && !diffProfile(baselineRef.current, latestRef.current).empty;
  }

  // ── Själva skrivningen ──
  async function writeProfile() {
    const cur = clone(latestRef.current);
    const before = baselineRef.current;
    const diff = diffProfile(before, cur);
    if (diff.empty) return [];

    const ref = doc(db, 'profiles', uid);
    const now = new Date();
    const stamp = { updatedAt: now, lastEditedBy: editorUid, lastEditedAt: now };
    const fv = { arrayUnion, arrayRemove };
    try {
      if (!existsRef.current) {
        await setDoc(ref, { ...cur, ...stamp }, { merge: true });
      } else if (needsTransaction(diff)) {
        await runTransaction(db, async tx => {
          const snap = await tx.get(ref);
          if (!snap.exists()) tx.set(ref, { ...cur, ...stamp });
          else tx.update(ref, { ...buildUpdate(diff, snap.data(), fv), ...stamp });
        });
      } else {
        await updateDoc(ref, { ...buildUpdate(diff, null, fv), ...stamp });
      }
    } catch (err) {
      err.saveKeys = diff.keys;
      throw err;
    }
    const existed = existsRef.current;
    existsRef.current = true;
    baselineRef.current = cur;

    // En version per redigeringssession: profilen som den var när sessionen började
    const t = Date.now();
    if (!sessionRef.current || t - sessionRef.current.lastChangeAt > SESSION_IDLE_MS) {
      sessionRef.current = { start: before, versioned: !existed };
    }
    sessionRef.current.lastChangeAt = t;
    if (!sessionRef.current.versioned) {
      try {
        await addDoc(collection(db, 'profiles', uid, 'versions'), {
          snapshot: sessionRef.current.start, createdAt: new Date(),
          createdBy: editorUid, label: versionLabel('Före redigering'),
        });
        sessionRef.current.versioned = true;
      } catch (err) {
        // Görs om vid nästa sparning i sessionen
        console.error('Kunde inte spara version av profilen', err);
      }
    }

    // Synka kanoniska fält till medarbetarregistret när de ändrats
    const sync = userSyncFields(cur.personal);
    const syncJson = JSON.stringify(sync);
    if (syncJson !== userSyncRef.current) {
      try {
        await updateDoc(doc(db, 'users', uid), sync);
        userSyncRef.current = syncJson;
      } catch (err) {
        console.error('Kunde inte synka profilen till medarbetarregistret', err);
      }
    }
    return diff.keys;
  }

  const autosave = useAutosave({
    write: writeProfile,
    hasChanges,
    ready: () => baselineRef.current !== null,
    logLabel: 'Profilen',
  });

  /** Startläge när profilen laddats. */
  function begin(baseline, { exists, updatedAt }) {
    baselineRef.current = clone(baseline);
    existsRef.current = exists;
    userSyncRef.current = JSON.stringify(userSyncFields(baseline.personal));
    sessionRef.current = null;
    if (updatedAt) autosave.setLastSavedAt(updatedAt);
  }

  /** Rensa och Återställ skapar egna versioner – ingen extra för sessionen. */
  function markVersioned() {
    sessionRef.current = { start: baselineRef.current, versioned: true, lastChangeAt: Date.now() };
  }

  const { setLastSavedAt: _, ...rest } = autosave;
  return { ...rest, begin, markVersioned };
}
